<!--
  Provisionssimulation (route /#/cp/dashboard/simulation).

  Two modes, three capture forms, one result. The forms are TRANSIENT form widgets
  (MvsFormDto -> mvs-widget), not hand-written form HTML; the framework renders the fields. The
  visible dimension fields come from the configuration of the Provisionsart, not from a fixed mask.

  The forms stay MOUNTED across a mode switch and are only hidden by CSS. An @if would destroy the
  widget, and a transient form has no source to reload its entries from.

  The Provisionsart chooser and the mode switch below are page-level controls, not fields of a
  business object: they pick WHICH form is built, so they cannot be part of it. That is why the two
  PrimeNG controls stand alone with ngModel instead of being wrapped by MvsFormField — the same
  construction as the filters of the overview pages.
-->
<div class="cp-provision-simulation">

    <!-- ══ Inputs ═════════════════════════════════════════════════════════════════════════════ -->
    <div class="surface-card border-round p-3 mb-3">

        <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">

            <div>
                <h2 class="text-lg font-semibold m-0">Provisionssimulation</h2>
                <!-- The subline follows the mode: mode B asks for a contract, not for dimensions. -->
                <div class="text-sm text-color-secondary mt-1">
                    @if (mode === MODE_EXISTING) {
                        Vertrag per VSNR suchen und Abrechnungsmonat wählen — die Rechenkette rechnet und
                        protokolliert, ohne zu buchen.
                    } @else {
                        Dimensionen, Vertragsbeginn und Berater eingeben — die Rechenkette rechnet und
                        protokolliert, ohne zu buchen.
                    }
                </div>
            </div>

            <div class="flex flex-wrap align-items-center gap-3">

                <div class="flex flex-column">
                    <label class="text-xs text-color-secondary mb-1" for="cpSimProvisionType">
                        Provisionsart
                    </label>
                    <p-select inputId="cpSimProvisionType"
                              [options]="provisionTypes"
                              [(ngModel)]="selectedProvisionTypeId"
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Provisionsart wählen"
                              [showClear]="false"
                              styleClass="w-18rem"
                              (onChange)="handleProvisionTypeChange()">
                    </p-select>
                </div>

                <div class="flex flex-column">
                    <label class="text-xs text-color-secondary mb-1">Betriebsart</label>
                    <p-selectButton [options]="modeOptions"
                                    [(ngModel)]="mode"
                                    optionLabel="label"
                                    optionValue="value"
                                    [allowEmpty]="false"
                                    (onChange)="handleModeChange()">
                    </p-selectButton>
                </div>

            </div>
        </div>

        @if (!selectedProvisionTypeId) {

            <p-message severity="info"
                       text="Provisionsart wählen — das Eingabeformular richtet sich nach ihrer Konfiguration.">
            </p-message>

        } @else if (busy && !simulationForm) {

            <p-skeleton height="2.5rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton height="2.5rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton height="2.5rem"></p-skeleton>

        } @else if (simulationForm) {

            <!--
              A blocking finding means: this Provisionsart cannot book anything at all. It stands
              ABOVE the form so nobody fills it in first and learns afterwards that the answer was
              already settled.
            -->
            @if (simulationForm.blockingFinding) {
                <p-message severity="error" styleClass="w-full mb-3">
                    <span class="flex align-items-start gap-2">
                        <i class="fa-solid fa-triangle-exclamation mt-1"></i>
                        <span>
                            <span class="font-semibold">Diese Provisionsart kann nichts buchen.</span><br/>
                            {{ simulationForm.blockingFinding }}
                        </span>
                    </span>
                </p-message>
            }

            <!-- The server's notice: first calculation without the stock, and the line that
                 separates it from the account's "simulierter Saldo", where "simuliert" means
                 something else. -->
            @if (simulationForm.notice) {
                <p-message severity="info" styleClass="w-full mb-3">
                    <span class="text-sm">{{ simulationForm.notice }}</span>
                </p-message>
            }

            @if (simulationForm.goLiveDate) {
                <div class="text-sm text-color-secondary mb-3">
                    <i class="fa-regular fa-flag-checkered mr-1"></i>
                    Go-live dieser Provisionsart:
                    <span class="font-semibold">{{ simulationForm.goLiveDate | date: 'dd.MM.yyyy' }}</span>
                    — ein Vertragsbeginn davor ist nicht provisionsrelevant.
                </div>
            }

            <!-- ── Mode A: hypothetical contract ─────────────────────────────────────────────── -->
            <div [class.hidden]="mode !== MODE_HYPOTHETICAL">

                @if (formWidgetMain) {
                    <mvs-widget [widgetData]="formWidgetMain"
                                (onGenericEvent)="handleMainFormEvent($event)">
                    </mvs-widget>

                    <!-- The price block is optional throughout; this line says what happens when it
                         stays empty, so nobody reads the four fields as new mandatory input. -->
                    <div class="text-sm text-color-secondary mt-2">
                        <i class="fa-regular fa-circle-info mr-1"></i>
                        Bruttobeitrag, Gebühren, Zahlbeitrag und Versicherungsteuersatz sind optional
                        — ohne Angabe rechnet die Simulation wie bisher allein mit dem Beitrag je
                        Zahlungsperiode. Der Steuersatz ist ein Prozentsatz, kein Betrag.
                    </div>
                }

                @if (formWidgetExtra) {
                    <div class="mt-2">

                        <p-button
                                [label]="(extraDimensionsVisible ? 'Weitere Dimensionen ausblenden' : 'Weitere Dimensionen') + ' (' + unoccupiedDimensions.length + ')'"
                                [icon]="extraDimensionsVisible ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                                severity="secondary"
                                size="small"
                                [text]="true"
                                pTooltip="Diese Dimensionen schränkt keine Zeile dieser Provisionsart ein — sie ändern das Ergebnis heute nicht."
                                tooltipPosition="top"
                                (onClick)="handleToggleExtraDimensions()">
                        </p-button>

                        <!-- Hidden by CSS, not by @if: otherwise entered values would be lost on collapse. -->
                        <div class="mt-2" [class.hidden]="!extraDimensionsVisible">
                            <div class="text-sm text-color-secondary mb-2">
                                Keine Zeile dieser Provisionsart schränkt diese Dimensionen ein. Eine
                                Eingabe hier ändert das Ergebnis daher heute nicht — sie wird aber
                                mitgeschickt und im Protokoll ausgewiesen.
                            </div>

                            <mvs-widget [widgetData]="formWidgetExtra"
                                        (onGenericEvent)="handleExtraFormEvent($event)">
                            </mvs-widget>
                        </div>
                    </div>
                }
            </div>

            <!-- ── Mode B: existing contract ─────────────────────────────────────────────────── -->
            <div [class.hidden]="mode !== MODE_EXISTING">

                <!--
                  The ordered, clearly visible notice. It stands above the form AND once more above
                  the result: for legacy contracts the first calculation deviates systematically
                  from the last run (measured: 230.208 of 236.755 rows carry a migration overwrite),
                  and whoever reads this only after the comparison has already reported the
                  difference as a defect.
                -->
                <p-message severity="warn" styleClass="w-full mb-3">
                    <span class="flex align-items-start gap-2">
                        <i class="fa-solid fa-triangle-exclamation mt-1"></i>
                        <span>
                            <span class="font-semibold">Erstberechnung ohne Bestand.</span>
                            Der Vertrag wird gerechnet, als würde er heute zum ersten Mal bepreist:
                            ohne manuelle Übersteuerungen, ohne eingefrorene Berater, ohne bereits
                            gebuchte Beträge. Bei Altbestandsverträgen weicht das Ergebnis deshalb
                            systematisch vom letzten Provisionslauf ab — das ist der Regelfall und
                            kein Rechenfehler.
                        </span>
                    </span>
                </p-message>

                @if (formWidgetContract) {
                    <mvs-widget [widgetData]="formWidgetContract"
                                (onGenericEvent)="handleContractFormEvent($event)">
                    </mvs-widget>
                }
            </div>

            <!-- ── Action bar ────────────────────────────────────────────────────────────────── -->
            <div class="flex flex-wrap align-items-center gap-2 mt-3">

                <p-button label="Simulieren"
                          icon="fa-regular fa-flask"
                          [disabled]="!submittable"
                          [loading]="busy"
                          (onClick)="handleSimulate()">
                </p-button>

                <span class="text-sm text-color-secondary">
                    Es wird nichts gebucht und nichts gespeichert.
                </span>
            </div>

            @if (errorMessage) {
                <p-message severity="error" styleClass="w-full mt-3" [text]="errorMessage"></p-message>
            }

        }
    </div>

    <!-- ══ Result ═════════════════════════════════════════════════════════════════════════════ -->
    @if (busy && simulationForm) {

        <div class="surface-card border-round p-3">
            <p-skeleton height="2rem" styleClass="mb-3"></p-skeleton>
            <p-skeleton height="6rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton height="10rem"></p-skeleton>
        </div>

    } @else if (result) {

        <div class="surface-card border-round p-3">

            <!-- State: EACH of the five is an answer, none is a failure of the screen. -->
            <div class="flex flex-wrap align-items-center gap-2 mb-2">

                @if (result.stateInfo?.label) {
                    <p-tag [value]="result.stateInfo.label"
                           [icon]="result.stateInfo.image"
                           [style]="chipStyle(result.stateInfo.backgroundColor, result.stateInfo.color)"
                           styleClass="cp-sim-tag">
                    </p-tag>
                }

                <!-- The name is what the reader knows the Provisionsart by; the technical alias
                     is one hover away for whoever needs it. -->
                <span class="font-semibold"
                      [pTooltip]="result.provisionTypeAlias ? 'Alias: ' + result.provisionTypeAlias : null"
                      tooltipPosition="top">
                    {{ result.provisionTypeName }}
                </span>

                @if (result.existingContractMode) {
                    <p-tag value="Bestehender Vertrag {{ result.contractLabel ?? result.contractId }}"
                           icon="fa-regular fa-file-contract"
                           styleClass="cp-sim-tag">
                    </p-tag>
                }
            </div>

            @if (stateExplanation) {
                <p-message severity="warn" styleClass="w-full mb-3">
                    <span class="text-sm">{{ stateExplanation }}</span>
                </p-message>
            }

            <!-- The server's fixed notice, permanently above the result. -->
            @if (result.notice) {
                <p-message severity="info" styleClass="w-full mb-3">
                    <span class="text-sm">{{ result.notice }}</span>
                </p-message>
            }

            <!-- Warnings of the calculation chain — in mode B always the first-calculation sentence. -->
            @for (warning of result.warnings; track $index) {
                <p-message severity="warn" styleClass="w-full mb-2">
                    <span class="text-sm">{{ warning }}</span>
                </p-message>
            }

            <!-- ── Inputs and key dates ──────────────────────────────────────────────────────── -->
            <div class="grid mb-3">

                <div class="col-12 lg:col-7">
                    <h3 class="text-base font-semibold mt-0 mb-2">Wirksame Dimensionen</h3>

                    @if (result.existingContractMode) {
                        <div class="text-sm text-color-secondary mb-2">
                            Die Werte stehen am Vertrag und werden von der Rechenkette dort gelesen.
                            Welche davon entschieden haben, sagen die Protokollschritte unten.
                        </div>
                    }

                    <div class="grid">
                        @for (dimension of result.dimensions; track dimension.key) {
                            <div class="col-12 md:col-6 py-1">
                                <div class="flex align-items-center gap-1">
                                    <span class="text-xs text-color-secondary">{{ dimension.label }}</span>
                                    @if (dimension.occupied) {
                                        <i class="fa-solid fa-asterisk text-xs text-color-secondary"
                                           pTooltip="Diese Dimension wird von mindestens einer Zeile dieser Provisionsart eingeschränkt."
                                           tooltipPosition="top"></i>
                                    }
                                </div>
                                <div class="flex align-items-center gap-2">
                                    <span class="font-semibold">{{ dimensionDisplayValue(dimension) }}</span>
                                    <!-- ENTERED / DERIVED / FROM_CONTRACT — the caption comes from the server. -->
                                    @if (dimension.originInfo?.label && dimension.valueId != null) {
                                        <p-tag [value]="dimension.originInfo.label"
                                               [icon]="dimension.originInfo.image"
                                               [style]="chipStyle(dimension.originInfo.backgroundColor, dimension.originInfo.color)"
                                               styleClass="cp-sim-tag">
                                        </p-tag>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <div class="col-12 lg:col-5">
                    <h3 class="text-base font-semibold mt-0 mb-2">Stichtage</h3>

                    <div class="grid">
                        @for (keyDate of result.keyDates; track keyDate.key) {
                            <div class="col-12 md:col-6 py-1">
                                <div class="text-xs text-color-secondary">{{ keyDate.label }}</div>
                                <div class="font-semibold">
                                    {{ formatKeyDate(keyDate.value, keyDate.unit) }}
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            <!-- ── Result: the Provisionssätze ───────────────────────────────────────────────── -->
            <h3 class="text-base font-semibold mt-0 mb-2">Ergebnis</h3>

            <cp-provision-simulation-entitlements
                    [entitlements]="result.entitlements">
            </cp-provision-simulation-entitlements>

            <p-divider></p-divider>

            <!-- ── The calculation protocol: the actual order ────────────────────────────────── -->
            <cp-provision-simulation-protocol
                    [steps]="result.steps"
                    [entitlements]="result.entitlements"
                    [messages]="result.messages"
                    [state]="result.state">
            </cp-provision-simulation-protocol>

        </div>

    } @else if (simulationForm) {

        <div class="surface-card border-round p-4 text-center text-color-secondary">
            <i class="fa-regular fa-flask text-3xl mb-2 block"></i>
            <div>Eingaben ausfüllen und „Simulieren“ wählen.</div>
        </div>

    }

</div>
